
import { StandardDialog } from "@/components/ui/standard-dialog";
import { format } from "date-fns";
import { Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toLocalDate } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";

interface GuestInfo {
  invite_id: string;
  display_name: string;
  avatar_url?: string | null;
  user_full_name?: string | null;
  guest_avatar_url?: string | null;
}

interface GuestAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  timeSlot: string;
  guests: GuestInfo[];
}

export const GuestAvailabilityModal = ({
  isOpen,
  onClose,
  date,
  timeSlot,
  guests
}: GuestAvailabilityModalProps) => {
  if (!isOpen) return null;
  
  const formattedDate = format(toLocalDate(date), 'EEEE, MMMM d, yyyy');
  
  const timeSlotLabels: Record<string, string> = {
    'morning': 'Morning',
    'afternoon': 'Afternoon',
    'evening': 'Evening'
  };
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'G';
  };

  return (
    <StandardDialog
      trigger={<span className="hidden">Open</span>}
      title={`Available Guests - ${formattedDate} ${timeSlotLabels[timeSlot] || timeSlot}`}
      className={cn(
        "sm:max-w-[450px]", 
        "rounded-lg", // Ensure rounded corners
        "overflow-hidden" // Prevent content from breaking rounded corners
      )}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className="space-y-4 p-4">
        {guests.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No guests available at this time.
          </div>
        ) : (
          <div className="space-y-2">
            {guests.map(guest => (
              <div 
                key={guest.invite_id} 
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50"
              >
                <Avatar className="h-10 w-10 border-2 border-white">
                  {guest.guest_avatar_url ? (
                    <AvatarImage 
                      src={guest.guest_avatar_url} 
                      alt={guest.display_name} 
                      className="object-cover object-center" 
                    />
                  ) : guest.avatar_url ? (
                    <AvatarImage 
                      src={guest.avatar_url} 
                      alt={guest.display_name}
                      className="object-cover object-center"
                    />
                  ) : null}
                  <AvatarFallback className="!bg-black text-white">
                    {getInitials(guest.display_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{guest.display_name}</p>
                  {guest.user_full_name && guest.user_full_name !== guest.display_name && (
                    <p className="text-sm text-gray-500">{guest.user_full_name}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </StandardDialog>
  );
};
